import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';

interface SimpleAudioRecorderProps {
  title: string;
  onAudioUploaded?: (audioUrl: string) => void;
  existingAudioUrl?: string;
}

const SimpleAudioRecorder: React.FC<SimpleAudioRecorderProps> = ({ title, onAudioUploaded, existingAudioUrl }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(existingAudioUrl || null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Update local state when existingAudioUrl prop changes
  useEffect(() => {
    setAudioUrl(existingAudioUrl || null);
    // Clear the blob when we get a new existing URL since it means it's already uploaded
    if (existingAudioUrl) {
      setAudioBlob(null);
    }
  }, [existingAudioUrl]);

  const startRecording = async () => {
    try {
      // Clear any previous recording when starting a new one
      if (audioUrl && audioBlob) {
        URL.revokeObjectURL(audioUrl);
      }
      setAudioUrl(null);
      setAudioBlob(null);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playAudio = () => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play();
    }
  };

  const uploadAudio = async () => {
    if (!audioBlob || !onAudioUploaded) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await api.post('/tours/upload-audio', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onAudioUploaded(response.data.audio_url);
      // Keep the local audio for playback but clear the blob since it's uploaded
      setAudioBlob(null);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload audio. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removeAudio = () => {
    // Clean up object URL to prevent memory leaks
    if (audioUrl && audioBlob) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setAudioBlob(null);
    if (onAudioUploaded) {
      onAudioUploaded('');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioUrl && audioBlob) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl, audioBlob]);

  return (
    <div className="p-3 border border-primary-200 rounded-lg bg-primary-50">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-blue-700">
          🎤 Audio Recording for {title}
        </div>
        {audioUrl && (
          <button
            type="button"
            onClick={removeAudio}
            className="text-red-500 hover:text-red-700 text-xs"
            title="Remove audio"
          >
            🗑️ Remove
          </button>
        )}
      </div>
      
      <div className="flex gap-2 flex-wrap">
        {!isRecording ? (
          <button
            type="button"
            onClick={startRecording}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            🎤 Record
          </button>
        ) : (
          <button
            type="button"
            onClick={stopRecording}
            className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
          >
            ⏹️ Stop
          </button>
        )}
        
        {audioUrl && (
          <button
            type="button"
            onClick={playAudio}
            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
          >
            ▶️ Play
          </button>
        )}

        {audioBlob && (
          <button
            type="button"
            onClick={uploadAudio}
            disabled={isUploading}
            className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:opacity-50"
          >
            {isUploading ? '⏳ Saving...' : '💾 Save'}
          </button>
        )}
      </div>
      
      {audioUrl && !audioBlob && (
        <div className="mt-2 text-xs text-green-600">
          ✅ Audio saved successfully
        </div>
      )}
      
      {audioUrl && audioBlob && (
        <div className="mt-2 text-xs text-yellow-600">
          ⚠️ Click "Save" to upload your recording
        </div>
      )}
    </div>
  );
};

export default SimpleAudioRecorder;